import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Listbox, Transition } from "@headlessui/react";
import { Fragment, ReactNode } from "react";
import { CommonProps } from "./types";
import { offset, size, useFloating } from "@floating-ui/react";
import { classNames } from "utils/common";

type Props<T> = {
  value: T | null;
  onChange?: (item: T | null) => void;
  renderLabel: (item: T | null) => ReactNode | string;
} & CommonProps<T>;

const SingleSelect = function <T>({
  items,
  value = null,
  disabled = false,
  onChange,
  renderOption,
  renderLabel,
  keyExtractor,
}: Props<T>) {
  const { refs, floatingStyles } = useFloating({
    placement: "bottom-start",
    middleware: [
      offset(8),
      size({
        apply({ availableHeight }) {
          if (refs.floating.current) {
            Object.assign(refs.floating.current.style, {
              maxHeight: `${availableHeight - 10}px`,
            });
          }
        },
      }),
    ],
  });

  const label = renderLabel(value);
  return (
    <Listbox
      as="div"
      value={value}
      onChange={onChange}
      disabled={disabled}
      className="relative"
    >
      <Listbox.Button as={Fragment}>
        {({ open }) => (
          <button
            ref={refs.setReference}
            className="inline-flex w-56 justify-between gap-x-1.5 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          >
            {typeof label === "string" ? (
              <p className="truncate">{label}</p>
            ) : (
              label
            )}
            <FontAwesomeIcon
              icon={faChevronDown}
              className={`-mr-1 h-5 w-5 text-gray-400 ${
                open ? "rotate-180 transform" : ""
              }}`}
              aria-hidden="true"
            />
          </button>
        )}
      </Listbox.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Listbox.Options
          ref={refs.setFloating}
          style={floatingStyles}
          className="absolute right-0 z-10 mt-2 w-56 origin-top-right overflow-y-auto rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
        >
          {items.map((item) => (
            <Listbox.Option key={keyExtractor(item)} value={item} as={Fragment}>
              {({ selected }) => (
                <li
                  className={classNames(
                    selected ? "text-gray-900" : "",
                    "cursor-pointer px-2 py-2 hover:bg-gray-100 hover:text-gray-900"
                  )}
                >
                  <div>{renderOption({ item, selected })}</div>
                </li>
              )}
            </Listbox.Option>
          ))}
        </Listbox.Options>
      </Transition>
    </Listbox>
  );
};

export default SingleSelect;
